import { DeploymentActionResource } from "../../../../client/resources/index";
import { ActionExecutionLocation } from "../../../../client/resources";
import * as React from "react";
import { ExpandableFormSection, Summary, StringRadioButtonGroup, RadioButton, Note, Text } from "components/form";
import ParseHelper from "utils/ParseHelper/ParseHelper";
import { RunOn, TargetRoles } from "areas/projects/components/DeploymentProcess/ActionDetails";
import { RoleChip } from "components/Chips";
import { RoleMultiSelect } from "components/MultiSelect";
import MaxParallelism from "./MaxParallelism";
import { MouseEvent } from "react";
import styles = require("./style.less");

interface ExecutionPlanProps {
    projectId: string;
    expandedByDefault: boolean;
    executionLocation: ActionExecutionLocation;
    runOn: RunOn;
    targetRoleOption: TargetRoles;
    targetRoles: string;
    isChildStep: boolean;
    maxParallelism: string;
    availableRoles: string[];
    targetRolesError: string;
    runsOnServer: boolean;
    onRunOnChanged(runOn: RunOn): void;
    onTargetRolesChanged(roles: string[]): void;
    onMaxParallelismChanged(max: string): void;
}

interface ExecutionPlanState {
    showWindowSize: boolean;
}

export default class ExecutionPlan extends React.Component<ExecutionPlanProps, ExecutionPlanState> {
    constructor(props: ExecutionPlanProps) {
        super(props);
        this.state = {
            showWindowSize: props.maxParallelism ? props.maxParallelism.length > 0 : false
        };
    }

    shouldComponentUpdate(nextProps: ExecutionPlanProps, nextState: ExecutionPlanState) {
        return nextProps.runOn !== this.props.runOn
            || nextProps.targetRoles !== this.props.targetRoles
            || nextProps.maxParallelism !== this.props.maxParallelism
            || nextProps.targetRolesError !== this.props.targetRolesError
            || nextState.showWindowSize !== this.state.showWindowSize;
    }

    render() {
        return <ExpandableFormSection
            isExpandedByDefault={this.props.expandedByDefault}
            errorKey="Octopus.Action.TargetRoles"
            title="Execution Plan"
            summary={this.executionPlanSummary()}
            help="Where should this step run?">
            {this.renderRunOnOptions()}
            {this.renderRolesOptions()}
        </ExpandableFormSection>;
    }

    private executionPlanSummary() {
        const summary = [<span>This step will run on</span>];
        let runOnServer = false;
        // keeping these as four cases not two and combining the if checks because I am
        // not sure of the priority of Octopus.Action.RunOnServer over props.executionLocation
        if (this.props.executionLocation === ActionExecutionLocation.AlwaysOnServer) {
            summary.push(<span> the <strong>Octopus Server</strong></span>);
            runOnServer = true;
        } else if (this.props.executionLocation === ActionExecutionLocation.AlwaysOnTarget) {
            summary.push(<span> <strong>deployment targets</strong></span>);
        } else if (this.props.runOn === RunOn.DeploymentTarget) {
            summary.push(<span> <strong>deployment targets</strong></span>);
        } else {
            summary.push(<span> the <strong>Octopus Server</strong></span>);
            runOnServer = true;
        }

        if (this.props.targetRoleOption === TargetRoles.None) {
            summary.push(<p/>);
            return Summary.summary(React.Children.toArray(summary));
        }

        const roles = ParseHelper.parseCSV(this.props.targetRoles);
        if (this.props.runOn !== RunOn.OctopusServer) {
            if (roles.length > 0) {
                summary.push(runOnServer
                    ? <span> on behalf of </span>
                    : roles.length > 1
                        ? <span> in roles</span>
                        : <span> in role</span>);
                roles.forEach(r => {
                    summary.push(<RoleChip role={r} key={"role-" + r}/>);
                });
            } else {
                summary.push(runOnServer
                    ? <span> on behalf of roles, but the roles have not been selected</span>
                    : <span> but the roles have not been selected</span>
                );
            }
        }

        summary.push(<p/>);

        if (this.props.maxParallelism) {
            summary.push(<span>This is a rolling step that will
                run {runOnServer ? "on behalf of" : "on"}&nbsp;
                <strong>{this.props.maxParallelism}</strong> target
                {this.props.maxParallelism !== "1" ? "s" : ""} at a
            time</span>);
        } else {
            if (!this.props.runsOnServer || roles.length > 0) {
                summary.push(<span>All deployments will be run in <strong>parallel</strong></span>);
            }
        }

        return Summary.summary(React.Children.toArray(summary));
    }

    private renderRunOnOptions = () => {
        const alwaysOnServer = this.props.executionLocation === ActionExecutionLocation.AlwaysOnServer;

        if (alwaysOnServer && this.props.targetRoleOption !== TargetRoles.Optional) {
            return <div>Run on the Octopus Server</div>;
        }

        if (this.props.executionLocation === ActionExecutionLocation.AlwaysOnTarget) {
            return <div>Run on deployment targets</div>;
        }

        return <StringRadioButtonGroup value={this.props.runOn} onChange={this.props.onRunOnChanged}>
            {!this.props.isChildStep && <RadioButton value={RunOn.OctopusServer} label="Octopus Server"/>}
            <RadioButton value={RunOn.OctopusServerForRoles} label="Octopus Server on behalf of roles"/>
            {!alwaysOnServer && <RadioButton value={RunOn.DeploymentTarget} label="Deployment targets" isDefault={true}/>}
        </StringRadioButtonGroup>;
    }

    private renderRolesOptions = () => {
        if (this.props.targetRoleOption === TargetRoles.None) {
            return <Note>This step will run once on the Octopus Server</Note>;
        }

        if (this.props.isChildStep) {
            return (this.props.runsOnServer)
                ? <p className="text-muted">This step is part of a rolling step, which
                    runs on the Octopus Server on behalf of deployment targets in the following
                    roles:
                    {this.roleList(this.props.targetRoles)}
                </p>
                : <p className="text-muted">This step is part of a rolling step, which
                    runs on deployment targets in the following roles:
                    {this.roleList(this.props.targetRoles)}
                </p>;
        }

        const nodes = [];
        if (this.props.runOn !== RunOn.OctopusServer) {
            nodes.push(<RoleMultiSelect
                onChange={this.props.onTargetRolesChanged}
                value={ParseHelper.parseCSV(this.props.targetRoles)}
                label={this.props.runOn === RunOn.DeploymentTarget ? "Runs on targets in roles" : "On behalf of target roles"}
                validate={roles => roles.length === 0 ? "Please enter one or more roles" : ""}
                error={this.props.targetRolesError}
                items={this.props.availableRoles} />);
        }

        if (this.props.runOn === RunOn.OctopusServer) {
            nodes.push(<Note><p className="text-muted">This step will run once on the Octopus Server</p></Note>);
        } else {
            if (this.state.showWindowSize) {
                nodes.push(<Note>
                    <p className="text-muted">
                        This is a rolling step that will run on <span>the Octopus Server on behalf of</span> a limited
                        number of deployment targets at once.&nbsp;
                        <a href="#"
                            onClick={(e: MouseEvent<{}>) => {
                                e.preventDefault();
                                this.setState({ showWindowSize: false });
                                this.props.onMaxParallelismChanged("");
                        }}>Deploy to all deployment targets in parallel</a>.
                    </p>
                </Note>);
                nodes.push(<MaxParallelism key="maxparallelism"
                    projectId={this.props.projectId}
                    value={this.props.maxParallelism}
                    onChange={(x) => this.props.onMaxParallelismChanged(x)} />);
            } else {
                nodes.push(<Note>
                        This step will be run on <span>
                        {this.props.runsOnServer ? "the Octopus Server on behalf of" : ""}
                        </span> all deployment targets in parallel.&nbsp;
                        <a href="#"
                            onClick={(e: MouseEvent<{}>) => {
                                e.preventDefault();
                                this.setState({ showWindowSize: true });
                                this.props.onMaxParallelismChanged("1");
                        }}>Configure a rolling deployment</a>.
                </Note>);
            }
        }

        return React.Children.toArray(nodes);
    }

    private roleList(csv: string) {
        const list = ParseHelper.parseCSV(csv);
        return list.map(r => <RoleChip role={r} key={"role-" + r}/>);
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/areas/projects/components/DeploymentProcess/ExecutionPlan.tsx